import { useEffect, useState } from 'react';
import { X, Check } from 'lucide-react';
import { NumberField } from '@renderer/components/ui/NumberField';

interface Props {
  nombre: string;
  unidad: string;
  teorico: number;
  valorInicial: number | null;
  onClose: () => void;
  onGuardar: (fisico: number | null) => void;
}

/**
 * Sprint 19 — Mini-modal focal para capturar la lectura física de un insumo
 * durante el cierre del día.
 *
 * UX: uno a la vez con teclado grande, preview de diferencia en tiempo real,
 * botón "Coincide con teórico" para casos sin discrepancia.
 */
export function ModalCapturarFisico({ nombre, unidad, teorico, valorInicial, onClose, onGuardar }: Props) {
  const [fisico, setFisico] = useState<number>(valorInicial ?? teorico);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const delta = fisico - teorico;
  const deltaAbs = Math.abs(delta);
  const deltaPct = teorico > 0 ? deltaAbs / teorico * 100 : 0;
  const cuadra = deltaPct < 5;
  const discrepancia = deltaPct >= 15;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="shrink-0 flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <h3 className="text-lg font-bold">Conteo físico</h3>
          <button onClick={onClose} className="btn-ghost p-2" aria-label="Cerrar">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-slate-500">Ingrediente</div>
            <div className="text-xl font-bold text-slate-800">{nombre}</div>
            <div className="text-sm text-slate-500">
              Teórico (calculado): <span className="font-mono font-semibold">{teorico.toFixed(2)} {unidad}</span>
            </div>
          </div>

          <div>
            <label className="label" htmlFor="fisico">
              ¿Cuánto hay en realidad? ({unidad})
            </label>
            <NumberField
              id="fisico"
              min={0}
              decimals={3}
              className="input text-2xl font-mono text-right py-3"
              value={fisico}
              onChange={setFisico}
              autoFocus
            />
          </div>

          {/* Atajo rápido */}
          <button
            type="button"
            onClick={() => setFisico(teorico)}
            className="btn-ghost w-full text-xs"
          >
            <Check className="h-3.5 w-3.5" />
            Coincide con teórico ({teorico.toFixed(2)} {unidad})
          </button>

          {/* Preview de diferencia */}
          <div className={`rounded-lg border p-3 ${
            cuadra ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
            discrepancia ? 'bg-red-50 border-red-200 text-red-800' :
            'bg-amber-50 border-amber-200 text-amber-800'
          }`}>
            <div className="font-semibold text-sm mb-1">
              {cuadra ? '🟢 Cuadra' : discrepancia ? '🔴 Discrepancia mayor' : '🟡 Diferencia menor'}
            </div>
            <div className="text-xs">
              Diferencia: <span className="font-mono font-bold">
                {delta >= 0 ? '+' : ''}{delta.toFixed(2)} {unidad}
              </span>{' '}
              ({deltaPct.toFixed(0)}%)
            </div>
            {!cuadra && (
              <div className="text-xs mt-1">
                Se registrará como <strong>AJUSTE</strong> al cerrar el día.
              </div>
            )}
          </div>
        </div>

        <div className="shrink-0 flex justify-between gap-2 px-5 py-3 border-t border-slate-200 bg-slate-50 rounded-b-xl">
          {valorInicial !== null && (
            <button
              onClick={() => {
                onGuardar(null);
              }}
              className="btn-ghost text-xs text-red-600"
              title="Quitar la lectura física capturada"
            >
              Quitar conteo
            </button>
          )}
          <div className="flex gap-2 ml-auto">
            <button onClick={onClose} className="btn-ghost">
              Cancelar
            </button>
            <button
              onClick={() => onGuardar(fisico)}
              className="btn-primary"
              style={{ minHeight: '44px' }}
            >
              <Check className="h-4 w-4" />
              Guardar conteo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
